import type { FunctionComponent, ReactEventHandler } from 'react';
import { sanityImage, sanityImageSrcSet } from '../../../../utils/sanityImage';

type DefaultImageProps = {
  src?: string;
  defaultImage: string;
  alt?: string;
  /**
   * Rendered CSS width in px, used to size the image at the CDN. Defaults to
   * the 90px product thumbnail used across the Orders screens.
   */
  width?: number;
};

/**
 * Renders a product image, falling back to a local placeholder when it fails
 * to load.
 *
 * Sanity-hosted images are routed through our own CDN and resized to the
 * rendered size. Order/box item images arrive from the orders API as bare
 * `cdn.sanity.io` originals (~484KB each); serving them at thumbnail size from
 * our CDN is roughly a 50x saving per image. Images hosted elsewhere are left
 * untouched.
 */
const DefaultImage: FunctionComponent<DefaultImageProps> = ({
  src = null,
  defaultImage,
  alt = undefined,
  width = 90,
}) => {
  const renderDefaultImage: ReactEventHandler<HTMLImageElement> = (e) => {
    // Clear srcSet too, otherwise the browser keeps resolving from it.
    e.currentTarget.srcset = '';
    e.currentTarget.src = defaultImage;
  };

  return (
    <img
      src={sanityImage(src, width) || defaultImage}
      srcSet={sanityImageSrcSet(src, width)}
      onError={renderDefaultImage}
      alt={alt}
    />
  );
};

export default DefaultImage;
